import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { Endpoint, Maybe, Page } from 'app/shared/graph';

import { StoreConfigService } from './store-config.service';
import { StoreEndpointComponent } from './store-endpoint.component';

@Component({
    selector: 'admin-store-endpoints',
    templateUrl: './store-endpoints-page.component.html',
    styleUrls: ['./store-endpoints-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, FormsModule, MatFormField, MatLabel, MatInput, MatError, MatHint, StoreEndpointComponent, MatIcon, SaveButtonComponent, ErrorComponent],
})
export class StoreEndpointsPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly storeConfigService = inject(StoreConfigService);

    storeId: string;
    storeConfig?: {
        exitPage: Page;
        loginPage: Page;
        correctionsCompletedPage: Page;
        cartRedirectionPage: Page;
        externalUploaderPage: Page;
        addToCartEndpoint: Endpoint;
        getProductsPriceEndpoint: Endpoint;
        getPriceEndpoint: Endpoint;
        onProjectDeletedEndpoint: Endpoint;
        statusUpdateEndpoint: Endpoint;
        getProductQuantitiesEndpoint: Endpoint;
        getAlternateProductsEndpoint: Endpoint;
        getProductDisplaySettingsEndpoint: Endpoint;
        uploadPostProcessingEndpoint: Endpoint;
    };

    urlPattern = /^https?:\/\/.+$/;
    urlExternalUploaderPattern = /^https?:\/\/[^?]+\?[^=]+=\{State\}$/;
    invalidUrlMessage = 'This url is not valid.';

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.subscribeWithGenericLoadingErrorHandling(this.storeConfigService.getStoreConfig(this.storeId), storeWithConfig => {
                const initializeEndpoint = (endpoint: Maybe<Endpoint> | undefined) => endpoint ? { ...endpoint } : { headers: [] };
                this.storeConfig = {
                    exitPage: storeWithConfig.config.exitPage,
                    loginPage: storeWithConfig.config.loginPage,
                    cartRedirectionPage: storeWithConfig.config.cartRedirectionPage,
                    externalUploaderPage: storeWithConfig.config.externalUploaderPage,
                    correctionsCompletedPage: storeWithConfig.config.correctionsCompletedPage,
                    addToCartEndpoint: initializeEndpoint(storeWithConfig.config.addToCartEndpoint),
                    getProductsPriceEndpoint: initializeEndpoint(storeWithConfig.config.getProductsPriceEndpoint),
                    getPriceEndpoint: initializeEndpoint(storeWithConfig.config.getPriceEndpoint),
                    onProjectDeletedEndpoint: initializeEndpoint(storeWithConfig.config.onProjectDeletedEndpoint),
                    statusUpdateEndpoint: initializeEndpoint(storeWithConfig.config.statusUpdateEndpoint),
                    getProductQuantitiesEndpoint: initializeEndpoint(storeWithConfig.config.getProductQuantitiesEndpoint),
                    getAlternateProductsEndpoint: initializeEndpoint(storeWithConfig.config.getAlternateProductsEndpoint),
                    getProductDisplaySettingsEndpoint: initializeEndpoint(storeWithConfig.config.getProductDisplaySettingsEndpoint),
                    uploadPostProcessingEndpoint: initializeEndpoint(storeWithConfig.config.uploadPostProcessingEndpoint),
                };
            });
        }));
    }

    ngOnDestroy(): void {
        super.unsubscribeSubscriptions();
    }

    save() {
        if (!this.storeConfig) {
            return;
        }

        this.subscribeWithGenericSavingErrorHandling(this.storeConfigService.updateStoreConfig({
            storeId: this.storeId,
            cartRedirectionPage: this.storeConfig.cartRedirectionPage,
            exitPage: this.storeConfig.exitPage,
            loginPage: this.storeConfig.loginPage,
            externalUploaderPage: this.storeConfig.externalUploaderPage,
            correctionsCompletedPage: this.storeConfig.correctionsCompletedPage,
            getPriceEndpoint: this.storeConfig.getPriceEndpoint,
            addToCartEndpoint: this.storeConfig.addToCartEndpoint,
            getProductsPriceEndpoint: this.storeConfig.getProductsPriceEndpoint,
            statusUpdateEndpoint: this.storeConfig.statusUpdateEndpoint,
            onProjectDeletedEndpoint: this.storeConfig.onProjectDeletedEndpoint,
            getProductQuantitiesEndpoint: this.storeConfig.getProductQuantitiesEndpoint,
            getAlternateProductsEndpoint: this.storeConfig.getAlternateProductsEndpoint,
            getProductDisplaySettingsEndpoint: this.storeConfig.getProductDisplaySettingsEndpoint,
            uploadPostProcessingEndpoint: this.storeConfig.uploadPostProcessingEndpoint,
        }));
    }
}
